import React, { useState } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogTitle, DialogContent, Box, Typography, Button } from "@mui/material";

const getCroppedImg = async (imageSrc, cropPixels) => {
  const image = new Image();
  image.src = imageSrc;

  await new Promise((resolve) => (image.onload = resolve));

  const canvas = document.createElement("canvas");
  canvas.width = cropPixels.width;
  canvas.height = cropPixels.height;
  const ctx = canvas.getContext("2d");

  ctx.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    cropPixels.width,
    cropPixels.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(new File([blob], "cropped-image.png", { type: "image/png" }));
    }, "image/png");
  });
};

const ImageCropperDialog = ({
  open,
  imageSrc,
  aspect = 1,
  onClose,
  onSave,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const handleSave = async () => {
    const file = await getCroppedImg(imageSrc, croppedAreaPixels);
    onSave(file);
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: { borderRadius: 3 },
      }}
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        Crop Image
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: 320,
            backgroundColor: "var(--app-text)",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={(_, croppedPixels) =>
              setCroppedAreaPixels(croppedPixels)
            }
          />
        </Box>

        <Box
          mt={3}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="caption" color="text.secondary">
            Drag to reposition • Scroll to zoom
          </Typography>

          <Box display="flex" gap="9px">
            {/* Cancel Button */}
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                width: "100px",
                height: "38px",
                borderRadius: "9px",
                padding: "14px 12px",
                opacity: 1,
                textTransform: "none",
                fontWeight: 500,
                borderColor: "#D0D5DD",
                color: "#344054",
                "&:hover": {
                  borderColor: "#D0D5DD",
                  backgroundColor: "var(--app-surface-2)",
                },
              }}
            >
              Cancel
            </Button>

            {/* Save Button */}
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!croppedAreaPixels}
              sx={{
                width: "100px",
                height: "38px",
                borderRadius: "9px",
                padding: "14px 12px",
                opacity: 1,
                textTransform: "none",
                fontWeight: 500,
                backgroundColor: "#5B5AFB",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#4A49E8",
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  backgroundColor: "var(--app-border)",
                  color: "var(--app-text-muted)",
                },
              }}
            >
              OK
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ImageCropperDialog;
